import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Phone,
  Search,
  Filter,
  MoreVertical,
  CheckCircle2,
  Clock,
  CheckSquare,
  MessageSquare,
  CalendarClock,
  Loader2,
  Trash2
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, className: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20' },
  contacted: { label: 'Contacted', icon: MessageSquare, className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  resolved: { label: 'Resolved', icon: CheckCircle2, className: 'bg-green-50 text-green-700 ring-green-600/20' }
};

const ClinicInquiriesPage = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: 'pending', notes: '' });

  // Fetch inquiries
  const { data: inquiriesData, isLoading, isError } = useQuery({
    queryKey: ['clinicInquiries'],
    queryFn: async () => {
      const response = await api.get('/clinic-inquiries');
      return response.data.data;
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/clinic-inquiries/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['clinicInquiries']);
      toast.success('Inquiry updated successfully');
      setIsUpdateModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update inquiry');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/clinic-inquiries/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['clinicInquiries']);
      toast.success('Inquiry deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete inquiry');
    }
  });

  const handleOpenUpdateModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    setUpdateForm({
      status: inquiry.status || 'pending',
      notes: inquiry.notes || ''
    });
    setIsUpdateModalOpen(true);
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (!selectedInquiry) return;
    updateMutation.mutate({
      id: selectedInquiry._id,
      data: updateForm
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this inquiry?')) {
      deleteMutation.mutate(id);
    }
  };

  const inquiries = inquiriesData || [];
  
  // Filtering
  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch = 
      inquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      inquiry.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-admin-ucla-500" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-64 items-center justify-center text-red-500">
        Error loading inquiries.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">Clinic Inquiries</h1>
          <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400 mt-1">
            Manage setup consultation requests from doctors and clinics.
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-admin-slate-800 rounded-xl shadow-sm border border-admin-slate-200 dark:border-admin-slate-700 overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-admin-slate-200 dark:border-admin-slate-700 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-admin-slate-400" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg bg-admin-slate-50 dark:bg-admin-slate-900 text-sm focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 transition-shadow"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-admin-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg py-2 pl-3 pr-8 bg-admin-slate-50 dark:bg-admin-slate-900 text-sm focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-admin-slate-50 dark:bg-admin-slate-900/50">
                <th className="py-3 px-4 text-xs font-semibold text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Date</th>
                <th className="py-3 px-4 text-xs font-semibold text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Contact Details</th>
                <th className="py-3 px-4 text-xs font-semibold text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Clinic & Pref Time</th>
                <th className="py-3 px-4 text-xs font-semibold text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Package</th>
                <th className="py-3 px-4 text-xs font-semibold text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-xs font-semibold text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
              {filteredInquiries.length > 0 ? (
                filteredInquiries.map((inquiry) => {
                  const statusConf = STATUS_CONFIG[inquiry.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConf.icon;
                  
                  return (
                    <tr key={inquiry._id} className="hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700/50 transition-colors">
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="text-sm text-admin-slate-600 dark:text-admin-slate-300">
                          {format(new Date(inquiry.createdAt), 'MMM dd, yyyy')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">{inquiry.name}</span>
                          <a href={`tel:${inquiry.phone}`} className="text-xs text-admin-ucla-600 hover:underline flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" />
                            {inquiry.phone}
                          </a>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          {inquiry.clinicType && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 w-fit capitalize">
                              {inquiry.clinicType}
                            </span>
                          )}
                          {inquiry.preferredTime && (
                            <span className="text-xs text-admin-slate-500 dark:text-admin-slate-400 flex items-center gap-1 capitalize">
                              <CalendarClock className="w-3 h-3" />
                              {inquiry.preferredTime}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {inquiry.package ? (
                          <div className="flex flex-col">
                            <span className="text-sm text-admin-slate-700 dark:text-admin-slate-300 line-clamp-1">{inquiry.package.name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-admin-slate-400">General Inquiry</span>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${statusConf.className}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {statusConf.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenUpdateModal(inquiry)}
                            className="p-1.5 text-admin-slate-500 hover:text-admin-ucla-600 hover:bg-admin-ucla-50 dark:hover:bg-admin-slate-700 rounded transition-colors"
                            title="Update Status & Notes"
                          >
                            <CheckSquare className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(inquiry._id)}
                            className="p-1.5 text-admin-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-admin-slate-700 rounded transition-colors"
                            title="Delete Inquiry"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-admin-slate-500 dark:text-admin-slate-400 text-sm">
                    No inquiries found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Update Modal */}
      {isUpdateModalOpen && selectedInquiry && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-admin-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-admin-slate-200 dark:border-admin-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-admin-slate-900 dark:text-admin-slate-100">Update Inquiry</h3>
              <button onClick={() => setIsUpdateModalOpen(false)} className="text-admin-slate-400 hover:text-admin-slate-600">
                &times;
              </button>
            </div>
            
            <form onSubmit={handleUpdateSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">Status</label>
                <select
                  value={updateForm.status}
                  onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-1">Internal Notes</label>
                <textarea
                  value={updateForm.notes}
                  onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                  rows="4"
                  placeholder="E.g. Called them on Monday, requested quote for standard package..."
                  className="w-full px-3 py-2 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md text-sm bg-white dark:bg-admin-slate-900"
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-admin-slate-600 hover:bg-admin-slate-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 text-sm font-medium bg-admin-ucla-600 hover:bg-admin-ucla-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicInquiriesPage;
